import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axiosInstance from "../../utils/axiosInstance";
import { API_ENDPOINTS } from "../../constants/config";
import { ArrowLeft, RefreshCw, Filter, X, AlertTriangle, Activity } from "lucide-react";

// Utility function to normalize IP address to IPv4
const normalizeIpToIPv4 = (ip) => {
  if (!ip || ip === '::1' || ip === 'localhost') {
    return ip;
  }
  
  // Handle IPv6-mapped IPv4 addresses (::ffff:192.168.1.1)
  if (ip.startsWith('::ffff:')) {
    return ip.replace('::ffff:', '');
  }
  
  // If it's a pure IPv6 address (not mapped), return null
  if (ip.includes(':') && !ip.match(/^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/)) {
    const ipv6MappedMatch = ip.match(/::ffff:(\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3})/);
    if (ipv6MappedMatch) {
      return ipv6MappedMatch[1];
    }
    return null;
  }
  
  // Already IPv4 format
  return ip;
};

function SchoolLogsDetail() {
  const { schoolId } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("errors");
  const [logs, setLogs] = useState([]);
  const [school, setSchool] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [expandedRow, setExpandedRow] = useState(null);
  const [filters, setFilters] = useState({
    status: "",
    method: "",
    url: "",
    platform: "",
    userType: "",
    ip: "",
    deviceOs: "",
    appVersion: ""
  });
  const [appVersionOptions, setAppVersionOptions] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    total: 0,
    totalPages: 0,
    pageSize: 50
  });
  useEffect(() => {
    fetchSchool();
  }, [schoolId]);

  useEffect(() => {
    axiosInstance
      .get(API_ENDPOINTS.logsAppVersions)
      .then((res) => setAppVersionOptions(res.data?.versions || []))
      .catch(() => setAppVersionOptions([]));
  }, []);

  useEffect(() => {
    fetchSchoolLogs();
  }, [schoolId, activeTab, pagination.page, filters]);

  const fetchSchool = async () => {
    if (!schoolId) return;
    try {
      const response = await axiosInstance.get(API_ENDPOINTS.schools);
      const schools = response.data.data?.schools || [];
      const foundSchool = schools.find(s => s._id === schoolId);
      setSchool(foundSchool || null);
      if (!foundSchool) {
        setError("School not found");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to fetch school details");
      setSchool(null);
    }
  };

  const fetchSchoolLogs = async () => {
    if (!schoolId) return;
    
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams();
      
      params.append("page", pagination.page);
      
      // Show all logs for this school (app + web, any user). schoolId is sent so backend
      // returns requests that had this schoolId (header/query); optionally narrow by userType.
      params.append("schoolId", schoolId);
      if (filters.platform) params.append("platform", filters.platform);
      if (filters.userType) {
        params.append("userType", filters.userType);
      }
      if (filters.ip) params.append("ip", filters.ip);
      if (filters.deviceOs) params.append("deviceOs", filters.deviceOs);
      if (filters.appVersion) params.append("appVersion", filters.appVersion);

      if (activeTab === "requests") {
        if (filters.status) params.append("status", filters.status);
        if (filters.method) params.append("method", filters.method);
        if (filters.url) params.append("url", filters.url);
      }

      const url =
        activeTab === "errors"
          ? API_ENDPOINTS.logsErrors(params.toString())
          : activeTab === "failures"
            ? API_ENDPOINTS.logsFailures(params.toString())
            : API_ENDPOINTS.logsRequests(params.toString());
      const response = await axiosInstance.get(url);
      
      let fetchedLogs = response.data.logs || [];
      
      // For requests tab, sort: success first (status < 400), then failures (400-499), errors last (status >= 500)
      if (activeTab === "requests") {
        fetchedLogs = fetchedLogs.sort((a, b) => {
          const aStatus = a.status;
          const bStatus = b.status;
          
          // Handle undefined/null status - treat as success (likely successful requests without explicit status)
          if (aStatus === undefined || aStatus === null) {
            if (bStatus === undefined || bStatus === null) {
              // Both undefined, sort by timestamp
              return new Date(b.timestamp) - new Date(a.timestamp);
            }
            // a is undefined, b has status - if b is error, a comes first (success)
            if (bStatus >= 500) return -1;
            if (bStatus >= 400) return -1; // a (success) comes before b (failure)
            return -1; // a (success) comes before b (success)
          }
          
          if (bStatus === undefined || bStatus === null) {
            // b is undefined, a has status - if a is error, b comes first (success)
            if (aStatus >= 500) return 1;
            if (aStatus >= 400) return 1; // b (success) comes before a (failure)
            return 1; // b (success) comes before a (success)
          }
          
          // Categorize: 0 = success (< 400), 1 = failure (400-499), 2 = error (>= 500)
          const getCategory = (status) => {
            if (status < 400) return 0; // Success
            if (status < 500) return 1; // Failure
            return 2; // Error
          };
          
          const aCategory = getCategory(aStatus);
          const bCategory = getCategory(bStatus);
          
          // Sort by category first (success < failure < error)
          if (aCategory !== bCategory) {
            return aCategory - bCategory;
          }
          
          // Within same category, sort by timestamp (newest first)
          return new Date(b.timestamp) - new Date(a.timestamp);
        });
      }
      
      setLogs(fetchedLogs);
      if (response.data.total !== undefined) {
        setPagination(prev => ({
          ...prev,
          page: response.data.page || 1,
          total: response.data.total || 0,
          totalPages: response.data.totalPages || 0
        }));
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to fetch logs");
      setLogs([]);
      setPagination(prev => ({ ...prev, page: 1, total: 0, totalPages: 0 }));
    } finally {
      setLoading(false);
    }
  };


  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const getStatusColor = (status) => {
    if (!status) return "bg-gray-500";
    if (status >= 500) return "bg-red-600";
    if (status >= 400) return "bg-orange-500";
    if (status >= 300) return "bg-blue-500";
    return "bg-green-500";
  };

  const tabs = [
    { id: "requests", label: "All Requests", icon: <Activity className="w-4 h-4" /> },
    { id: "failures", label: "Failed Requests", icon: <AlertTriangle className="w-4 h-4" /> },
    { id: "errors", label: "Errors", icon: <AlertTriangle className="w-4 h-4" /> },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate("/dashboard/system-logs")}
            className="text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h2 className="text-2xl font-bold">{school?.name || "School Logs"}</h2>
            {school?.domain && (
              <p className="text-sm text-gray-500">{school.domain}</p>
            )}
          </div>
        </div>
        <button
          onClick={fetchSchoolLogs}
          disabled={loading}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-blue-700 transition-colors flex items-center disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      <div className="bg-white rounded-lg shadow-md">
        <div className="border-b border-gray-200">
          <div className="flex space-x-1 p-2">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center px-4 py-2 rounded-lg font-medium transition-colors ${
                  activeTab === tab.id
                    ? "bg-blue-600 text-white"
                    : "text-gray-600 hover:bg-gray-100"
                }`}
              >
                {tab.icon}
                <span className="ml-2">{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        {(activeTab === "requests" || activeTab === "errors" || activeTab === "failures") && (
          <div className="p-4 border-b border-gray-200 bg-gray-50">
            <div className="flex items-center mb-2">
              <Filter className="w-4 h-4 mr-2 text-gray-600" />
              <span className="font-medium text-gray-700">Filters</span>
            </div>
            <div className={`grid grid-cols-1 ${activeTab === "requests" ? "md:grid-cols-7" : "md:grid-cols-4"} gap-3`}>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Platform</label>
                <select
                  value={filters.platform}
                  onChange={(e) => setFilters({ ...filters, platform: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All</option>
                  <option value="app">App</option>
                  <option value="web">Web</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">User Type</label>
                <select
                  value={filters.userType}
                  onChange={(e) => setFilters({ ...filters, userType: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All</option>
                  <option value="student">Student</option>
                  <option value="teacher">Teacher</option>
                  <option value="schooladmin">School Admin</option>
                  <option value="staff">Staff</option>
                  <option value="vehicle">Vehicle</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">IP Address</label>
                <input
                  type="text"
                  value={filters.ip}
                  onChange={(e) => setFilters({ ...filters, ip: e.target.value })}
                  placeholder="Search by IP"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Device OS</label>
                <select
                  value={filters.deviceOs}
                  onChange={(e) => setFilters({ ...filters, deviceOs: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All</option>
                  <option value="android">Android</option>
                  <option value="ios">iOS</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">App Version</label>
                <select
                  value={filters.appVersion}
                  onChange={(e) => setFilters({ ...filters, appVersion: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All</option>
                  {appVersionOptions.map((v) => (
                    <option key={v} value={v}>{v}</option>
                  ))}
                </select>
              </div>
              {activeTab === "requests" && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status Code</label>
                    <input
                      type="number"
                      value={filters.status}
                      onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                      placeholder="e.g., 500"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Method</label>
                    <select
                      value={filters.method}
                      onChange={(e) => setFilters({ ...filters, method: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">All</option>
                      <option value="GET">GET</option>
                      <option value="POST">POST</option>
                      <option value="PUT">PUT</option>
                      <option value="PATCH">PATCH</option>
                      <option value="DELETE">DELETE</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">URL Contains</label>
                    <input
                      type="text"
                      value={filters.url}
                      onChange={(e) => setFilters({ ...filters, url: e.target.value })}
                      placeholder="e.g., /api/users"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </>
              )}
            </div>
            {(filters.status || filters.method || filters.url || filters.platform || filters.userType || filters.ip || filters.deviceOs || filters.appVersion) && (
              <button
                onClick={() => {
                  setFilters({ ...filters, status: "", method: "", url: "", platform: "", userType: "", ip: "", deviceOs: "", appVersion: "" });
                  setPagination({ page: 1, total: 0, totalPages: 0 });
                }}
                className="mt-3 text-sm text-blue-600 hover:text-blue-800 flex items-center"
              >
                <X className="w-4 h-4 mr-1" />
                Clear Filters
              </button>
            )}
          </div>
        )}

        {error && (
          <div className="p-4 bg-red-50 border-l-4 border-red-500 text-red-700">
            {error}
          </div>
        )}

        <div className="p-4">
          {loading ? (
            <div className="text-center py-8">
              <RefreshCw className="w-8 h-8 animate-spin mx-auto text-blue-600" />
              <p className="mt-2 text-gray-600">Loading logs...</p>
            </div>
          ) : logs.length > 0 ? (
            <div className="overflow-x-auto">
              <div className="mb-4 flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  Total Logs: <span className="font-semibold">{pagination.total}</span> | 
                  Success: <span className="font-semibold text-green-600">{logs.filter(l => !l.status || l.status < 400).length}</span> | 
                  Failures: <span className="font-semibold text-orange-600">{logs.filter(l => l.status && l.status >= 400 && l.status < 500).length}</span> | 
                  Errors: <span className="font-semibold text-red-600">{logs.filter(l => l.status && l.status >= 500).length}</span>
                </div>
                {activeTab === "requests" && (
                  <div className="text-xs text-gray-500 bg-blue-50 px-2 py-1 rounded">
                    ✓ Sorted: Success → Failures → Errors
                  </div>
                )}
              </div>
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Timestamp</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Platform</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Device OS</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">App Version</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Method</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">URL</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Message</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Response Time</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">IP Address</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payload</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {logs.map((log, index) => {
                    const isSuccess = log.status && log.status < 400;
                    const isFailure = log.status && log.status >= 400;
                    return (
                      <React.Fragment key={index}>
                      <tr
                        className={`hover:bg-gray-50 transition-colors ${
                          isSuccess ? 'bg-green-50/30' : isFailure ? 'bg-red-50/30' : ''
                        }`}
                      >
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                          <div className="flex items-center">
                            {isSuccess && <span className="w-2 h-2 rounded-full bg-green-500 mr-2" title="Success"></span>}
                            {isFailure && <span className="w-2 h-2 rounded-full bg-red-500 mr-2" title="Failure"></span>}
                            {formatDate(log.timestamp)}
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm">
                          {log.platform ? (
                            <span className={`px-2 py-1 text-xs font-medium rounded ${
                              log.platform === 'app' 
                                ? 'bg-purple-100 text-purple-800' 
                                : log.platform === 'web'
                                ? 'bg-green-100 text-green-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {log.platform.toUpperCase()}
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm">
                          {log.deviceOs ? (
                            <span className={`px-2 py-1 text-xs font-medium rounded ${
                              log.deviceOs === 'android'
                                ? 'bg-emerald-100 text-emerald-800'
                                : 'bg-slate-100 text-slate-800'
                            }`}>
                              {log.deviceOs === 'android' ? 'Android' : 'iOS'}
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                          {log.appVersion || <span className="text-gray-400">-</span>}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm">
                          {log.method ? (
                            <span className="px-2 py-1 text-xs font-medium rounded bg-blue-100 text-blue-800">
                              {log.method}
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 max-w-xs truncate" title={log.url}>
                          {log.url || "-"}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 max-w-md" title={log.error?.message || log.error?.name || log.message}>
                          <div className="truncate">
                            {log.error?.message || log.error?.name || log.message || "-"}
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm">
                          {log.status ? (
                            <span className={`px-2 py-1 text-xs font-medium rounded text-white ${getStatusColor(log.status)}`} title={`HTTP ${log.status}`}>
                              {log.status}
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {log.responseTime || "-"}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        {log.userInfo ? (
                          <div className="flex flex-col">
                            <span className="text-gray-900 font-medium">
                              {log.userInfo.displayName || 'Unknown'}
                            </span>
                            {log.userInfo.type && log.userInfo.type !== 'anonymous' && (
                              <span className="text-xs text-gray-500 capitalize">
                                {log.userInfo.type}
                              </span>
                            )}
                          </div>
                        ) : log.userId && log.userId !== 'anonymous' ? (
                          <span className="text-gray-900 font-medium" title={log.userId}>
                            {log.userId.length > 30 ? `${log.userId.substring(0, 30)}...` : log.userId}
                          </span>
                        ) : (
                          <span className="text-gray-400 italic">Anonymous</span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {(() => {
                          const normalizedIp = normalizeIpToIPv4(log.ip);
                          if (normalizedIp && normalizedIp !== '::1' && normalizedIp !== 'localhost') {
                            return <span className="text-gray-900 font-mono text-xs">{normalizedIp}</span>;
                          } else if (log.ip === '::1' || normalizedIp === '::1') {
                            return <span className="text-gray-500" title="Localhost">localhost</span>;
                          } else {
                            return <span className="text-gray-400">-</span>;
                          }
                        })()}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        {log.request && (log.request.body || log.request.params || log.request.query) ? (
                          <button
                            onClick={() => setExpandedRow(expandedRow === index ? null : index)}
                            className="px-2 py-1 text-xs font-medium rounded bg-indigo-100 text-indigo-700 hover:bg-indigo-200 transition-colors"
                          >
                            {expandedRow === index ? "Hide" : "View"}
                          </button>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                    </tr>
                    {expandedRow === index && log.request && (
                      <tr className="bg-gray-900">
                        <td colSpan="11" className="px-4 py-3">
                          <div className="text-xs text-gray-400 mb-1 font-mono">Request Payload</div>
                          <pre className="text-xs text-green-300 font-mono overflow-auto max-h-60 whitespace-pre-wrap break-all">
                            {JSON.stringify(log.request, null, 2)}
                          </pre>
                        </td>
                      </tr>
                    )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">No logs found</div>
          )}
          
          {logs.length > 0 && pagination.totalPages > 1 && (
            <div className="mt-4 flex items-center justify-between border-t border-gray-200 pt-4">
              <div className="text-sm text-gray-700">
                Showing {((pagination.page - 1) * pagination.pageSize) + 1} to {Math.min(pagination.page * pagination.pageSize, pagination.total)} of {pagination.total} results
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                  disabled={pagination.page === 1}
                  className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-700">
                  Page {pagination.page} of {pagination.totalPages}
                </span>
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                  disabled={pagination.page >= pagination.totalPages}
                  className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default SchoolLogsDetail;
